import {MEDIA_KEYS} from "@/api/mediaApi";
import SourceContentIcon from "@/assets/colorable/source_content.svg?react";
import StoryIcon from "@/assets/colorable/story.svg?react";
import Modal from "@/components/Common/Modal";
import useContentDownload from "@/hooks/useContentDownload";
import {useFetchUser} from "@/hooks/useFetchUser";
import usePackageLimits from "@/hooks/usePackageLimits";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import {client} from "@/httpClient";
import useCampaignManagementStore from "@/store/campaignManagement";
import {purchaseModalContentAtom} from "@/store/jotaiAtoms";
import type {TInstagramMedia} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import dayjs from "dayjs";
import {useAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const getContentTypeText = (mediaType: "FEED" | "REELS" | "SOURCE" | "STORY" | "TIKTOK" | any) => {
  switch (mediaType) {
    case "FEED":
      return "post";
    case "REELS":
      return "reel";
    case "SOURCE":
      return "source";
    case "STORY":
      return "story";
    case "TIKTOK":
      return "TIKTOK";
    default:
      return "post";
  }
};

const ContentDownloader: FC = () => {
  const {t} = useTranslation("common");
  const managementCampaign = useCampaignManagementStore(state => state.campaign);
  const formatPrice = usePriceFormat();
  const [{buyoutsPerContent, contents, onPurchased}, setPurchaseModalContent] = useAtom(purchaseModalContentAtom);
  const content = contents[0];

  const campaign = content?.campaign ?? content?.instagram_media?.campaign_application?.campaign ?? managementCampaign;
  const campaignId = campaign?.id;
  const campaignTitle = campaign?.title;
  const {downloadMedia} = useContentDownload(contents, campaignTitle);
  const isSourceContent = content?.instagram_media?.media_product_type === "SOURCE";
  const {refetch: refetchUser} = useFetchUser();
  const {mutateAsync: purchaseContent} = useMutation({
    mutationKey: isSourceContent ? MEDIA_KEYS.mMediaPurchaseBulk : MEDIA_KEYS.mMediaPurchase,
    mutationFn: async () => {
      if (!isSourceContent) {
        await client.post(MEDIA_KEYS.mMediaPurchase[0], {
          campaign: campaignId,
          [content.instagram_media?.media_product_type === "TIKTOK" ? "tiktok_media" : "instagram_media"]: content.id,
        });
      } else {
        await client.post(MEDIA_KEYS.mMediaPurchaseBulk[0], {
          campaign: campaignId,
          source_material: contents.flatMap(content =>
            content.instagram_media?.media_type === "CAROUSEL_ALBUM"
              ? (content.instagram_media.children ?? []).map(child => child.id)
              : [content.instagram_media?.id],
          ),
        });
      }
      onPurchased && onPurchased();
      await downloadMedia();
      await refetchUser();
    },
  });
  const contentPieces: TInstagramMedia[] = isSourceContent
    ? contents.map(sourceContent => sourceContent.instagram_media!)
    : content.instagram_media?.media_type === "CAROUSEL_ALBUM"
      ? (content.instagram_media?.children ?? [])
      : [content!.instagram_media!];
  const {remainingBuyouts} = usePackageLimits();
  const contentType = getContentTypeText(
    content && content.instagram_media?.media_type !== "CAROUSEL_ALBUM"
      ? content.instagram_media?.media_product_type
      : contentPieces[0].media_product_type,
  );

  return (
    <Modal
      onClose={() => setPurchaseModalContent({buyoutsPerContent: 1, contents: []})}
      onConfirm={async () => {
        await purchaseContent();
      }}
      modalTitle={t("purchaseModalHeader")}
      confirmButtonText={t("useBuyout")}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <img className="size-8 rounded-full" src={content.user?.picture!} alt="Influencer profile" />
          <span className="text-neutral-black text-xs font-semibold">{content.user?.first_name}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {contentPieces.map(piece => (
            <img
              key={piece.id}
              src={piece.thumbnail_file ?? piece.media_file!}
              alt="Content thumbnail"
              className="size-24 rounded-md"
            />
          ))}
        </div>
        <div className="flex items-center gap-1">
          <StoryIcon className="text-neutral-grey-dark size-3 shrink-0" />
          <span className="text-neutral-grey-dark text-2xs pt-0.5">
            {t(contentType as "story") +
              " • " +
              dayjs(content?.instagram_media?.created).format("DD.MM.YYYY") +
              " • " +
              campaignTitle}
          </span>
        </div>
      </div>

      <div className="flex flex-col items-start justify-center gap-1 leading-none">
        <div className="flex items-center gap-1.5">
          <span className="text-primary-dark-solid text-4xl font-semibold">-{buyoutsPerContent}</span>
          <SourceContentIcon className="text-primary-dark-solid size-8" />
        </div>
        <div className="text-neutral-grey-dark text-2xs">
          {t("xContentBuyoutsRemaining", {count: remainingBuyouts - 1})}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <div className="overlow-hidden rounded-md bg-linear-(--primary-dark-gradient) p-px">
          <div className="rounded-md bg-white p-3">
            <span className="bg-linear-(--primary-dark-gradient) bg-clip-text text-xs text-transparent">
              {t("purchaseThanks")}
            </span>
          </div>
        </div>
        <div className="border-neutral-white-dark rounded-md border p-3">
          <span className="text-neutral-grey-dark text-xs">{t("purchaseLicense")}</span>
        </div>
        <div className="border-neutral-white-dark rounded-md border p-3">
          <span className="text-neutral-grey-dark text-xs">
            {t("purchaseInfluencerReceives", {
              storyPrice: formatPrice(16),
              reelPrice: formatPrice(49),
            })}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default ContentDownloader;
